import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { getLocalized, HealthLang } from "../health/healthTypes";
import {
    healthCol,
    healthColStack,
    healthContentPadX,
    healthHeroDesktopLayout,
    healthHeroGrid,
    healthHeroLoopWrap,
    healthHeroSectionY,
    healthSectionInnerStyle,
    healthSectionOuter,
} from "../health/healthLayout";
import {
    healthFocusVisible,
    healthHeroSubtitle,
    healthHeroTitle,
    healthPrimaryButton,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
    healthSectionEyebrow,
} from "../health/healthVisualStyles";
import { scrollToHealthHashAnchor } from "../health/healthScroll";
import { agentsLocalePath } from "./agentsLocalePath";

const imagePath = "image/robotics/";

interface AgentsHeroProps {
    data: DataKeys;
}

export function AgentsHero({ data }: AgentsHeroProps) {
    const { lang } = useLang();
    const navigate = useNavigate();
    const hLang = lang as HealthLang;
    const titleLead = getLocalized(data?.titleLead, hLang);
    const titleAccent = getLocalized(data?.titleAccent, hLang);
    const titleFallback = getLocalized(data?.title, hLang);
    const imageName = data?.image as string | undefined;
    const imageAlt =
        getLocalized(data?.imageAlt, hLang) ||
        getLocalized(data?.title, hLang);
    const primaryHref = agentsLocalePath(
        (data?.ctaPrimary?.href as string) ?? "/agents/aiia",
        hLang
    );
    const secondaryAnchor = (data?.ctaSecondary?.anchor as string) ?? "#product-suite";

    return (
        <header
            className={`${healthSectionOuter} relative isolate w-full overflow-x-clip bg-white overflow-y-visible`}
        >
            <div className="m-auto w-full" style={healthSectionInnerStyle}>
                <div
                    className={`${healthContentPadX} ${healthHeroSectionY} ${healthHeroDesktopLayout}`}
                >
                    <div className={healthHeroGrid}>
                        <div className={`${healthCol} ${healthColStack}`}>
                            <p className={healthSectionEyebrow}>
                                {getLocalized(data?.eyebrow, hLang)}
                            </p>
                            <h1 className={`${healthHeroTitle} [text-align:left]`}>
                                {titleLead && titleAccent ? (
                                    <>
                                        {titleLead}{" "}
                                        <span className="text-teal-600">{titleAccent}</span>
                                    </>
                                ) : (
                                    titleFallback
                                )}
                            </h1>
                            <p className={`${healthHeroSubtitle} max-w-xl [text-align:left]`}>
                                {getLocalized(data?.subtitle, hLang)}
                            </p>
                            <div className="flex w-full flex-col gap-3 pt-2 max-lg:items-stretch lg:flex-row lg:flex-nowrap lg:items-center lg:gap-4">
                                <Link
                                    to={primaryHref}
                                    className={`${healthPrimaryButton} ${healthFocusVisible} lg:shrink-0`}
                                >
                                    {getLocalized(data?.ctaPrimary?.label, hLang)}
                                </Link>
                                <a
                                    href={secondaryAnchor}
                                    onClick={(event) => {
                                        scrollToHealthHashAnchor(
                                            secondaryAnchor,
                                            event,
                                            true,
                                            (to, options) => navigate(to, options)
                                        );
                                    }}
                                    className={`${healthSecondaryButton} ${healthFocusVisible} lg:shrink-0 lg:whitespace-nowrap`}
                                >
                                    <span className={healthButtonLabel}>
                                        {getLocalized(data?.ctaSecondary?.label, hLang)}
                                    </span>
                                    <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                                </a>
                            </div>
                        </div>
                        {imageName && (
                            <div className={`${healthCol} ${healthHeroLoopWrap}`}>
                                <img
                                    src={require(`../../${imagePath}${imageName}`)}
                                    alt={imageAlt}
                                    className="h-auto w-full max-h-[min(440px,52vh)] object-contain object-center drop-shadow-[0_16px_48px_rgba(3,124,132,0.14)]"
                                    loading="eager"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}
